import { useLingui } from "@lingui/react/macro";
import { formatRuleMessageValues } from "@offstreetparking/design-system/formatters";
import { Button } from "@offstreetparking/design-system/forms";
import { NoParkingIcon } from "@offstreetparking/design-system/icons/NoParkingIcon";
import { useProductTracking } from "@offstreetparking/design-system/providers";
import { getRuleErrorDescriptor } from "@offstreetparking/domain/entities/rule/rule-messages";
import { useEffect, useId, useRef, useState } from "react";
import { offstreetClient } from "../../api/client";
import { usePortalFlowHost } from "../../host";
import { useCompany } from "../../providers/company";
import { useParker } from "../../providers/parker";
import { FormErrorAlert, GeneralErrorAlert, NotFoundAlert } from "../forms/Alert";
import { ButtonRow } from "../layouts/ButtonRow";
import { useKiosk } from "../providers/KioskProvider";
import { type PermitFormData, usePermitActiveStep, usePermitFormActions } from "./PermitContextProvider";
import { useIsProcessingPermit, usePermitError } from "./PermitContextProvider/PermitContextProvider";
import { ExistingPermitModal } from "./components/ExistingPermitModal";
import { KioskStartOver } from "./components/KioskStartOver";
import { ParkingSummary } from "./components/Summary";
import { useAccountPrefill } from "./hooks/useAccountPrefill";
import { useCreatePermitSteps } from "./hooks/useCreatePermitSteps";
import { useSavedPermits } from "./hooks/useSavedPermits";
import { useScrollToActiveStep } from "./hooks/useScrollToActiveStep";
import type { OverrideNotices, PermitTypeDto, TenantInfo } from "./types";
type CreatePermitWizardProps = {
	permitType: PermitTypeDto;
	overrideNotices?: OverrideNotices;
	onSelectOverridePermitType?: (permitTypeId: number) => void;
	preSelectedLocationId?: number;
	entryLookupId?: string;
	tenantId?: number;
	registrationInstructions?: string;
	selectedTenant?: TenantInfo;
};

export const CreatePermitWizard = ({
	permitType,
	overrideNotices,
	onSelectOverridePermitType,
	preSelectedLocationId,
	entryLookupId,
	tenantId,
	registrationInstructions,
	selectedTenant,
}: CreatePermitWizardProps) => {
	const { i18n, t } = useLingui();
	useAccountPrefill();
	const { addPermit } = useSavedPermits();
	const formId = useId();
	const { sectionRef, stepContentRef } = useScrollToActiveStep();
	const errorRef = useRef<HTMLDivElement>(null);
	const { mutate: createPermit } = offstreetClient.permit.createPermitPublic.useMutation();
	const { mutate: sendEmailPermitAndScheduleReminder } =
		offstreetClient.permit.sendEmailPermitAndScheduleReminder.useMutation();
	const { mutate: sendSmsPermitAndScheduleReminder } = offstreetClient.permit.sendSmsPermit.useMutation();
	const timezone = permitType.timezone;
	const error = usePermitError();
	const activeStepId = usePermitActiveStep();
	const { company: portalCompany } = useCompany();
	const { globalParkerFlag } = useParker();
	const optionalParkerAccounts = !!globalParkerFlag || !!portalCompany?.isOptionalParkerAccountsEnabled;
	const { isKiosk, startOverLookupId } = useKiosk();
	const { SignInPrompt } = usePortalFlowHost();
	const isProcessingPermit = useIsProcessingPermit();
	const { goNext, goToStep, getPermitPostData, setError, setIsProcessingPermit } = usePermitFormActions();
	const savedPermitLookupId = entryLookupId ?? permitType.locationLookupLink?.id;
	const { capture } = useProductTracking();
	const [duplicatedPermit, setDuplicatedPermit] = useState<{
		plate: string;
		permitIntervals: PermitFormData["permitIntervals"];
		timezone: string;
		publicIdentifier: string;
	}>();
	const steps = useCreatePermitSteps({
		formId,
		permitType,
		overrideNotices,
		onSelectOverridePermitType,
		preSelectedLocationId,
		savedPermitLookupId,
		tenantId,
		registrationInstructions,
		selectedTenant,
		onSubmit: (formData) => {
			setError(undefined);
			if (!goNext) {
				return;
			}
			setIsProcessingPermit(true);
			const { hasReachedEnd, hasPaymentsStep } = goNext((currentPermit) => ({
				...currentPermit,
				...formData,
				permitTypeId: permitType.permitTypeId,
			}));
			if (hasReachedEnd) {
				const permit = getPermitPostData();
				if (hasPaymentsStep) {
					if (permit.phoneNumber) {
						sendSmsPermitAndScheduleReminder({
							publicIdentifier: formData.publicIdentifier!,
							phoneNumber: permit.phoneNumber,
						});
					}
					if (permit.email) {
						if (permitType.smsExpiryNotifications) {
							capture(
								{
									feature: "sms_notifications",
									action: "register",
									object: "button",
									context: "permit_form",
								},
								{
									hasPhoneNumber: !!permit.phoneNumber,
									companyId: permitType.company.id,
								},
							);
						}
						sendEmailPermitAndScheduleReminder({
							publicIdentifier: formData.publicIdentifier!,
							emails: [permit.email],
						});
						goNext((prev) => ({
							...prev,
							publicIdentifier: formData.publicIdentifier,
						}));
						setIsProcessingPermit(false);
					}
				} else {
					createPermit(
						{
							...permit,
							...formData,
							tenantId,
						},
						{
							onSuccess: (data) => {
								if (permitType.smsExpiryNotifications) {
									capture(
										{
											feature: "sms_notifications",
											action: "register",
											object: "button",
											context: "permit_form",
										},
										{
											hasPhoneNumber: !!permit.phoneNumber,
											companyId: permitType.company.id,
										},
									);
								}
								if ((permitType.maxVehicles ?? 1) > 1) {
									capture(
										{
											feature: "multi_plate",
											action: "register",
											object: "button",
											context: "permit_form",
										},
										{
											plates: data.permit_vehicles_vehicle.length,
										},
									);
								}
								if (savedPermitLookupId) {
									addPermit(savedPermitLookupId, {
										publicIdentifier: data.publicIdentifier,
										publicAccessToken: data.publicAccessToken ?? "",
									});
								}
								if (data?.existingPermit) {
									setDuplicatedPermit({
										plate: data.permit_vehicles_vehicle[0]?.vehicle.plate!,
										permitIntervals: data.permitIntervals.map((interval) => ({
											startDateTimeISO: interval.startDateTime,
											endDateTimeISO: interval.endDateTime,
										})),
										timezone,
										publicIdentifier: data.publicIdentifier,
									});
								} else {
									goNext((prev) => ({
										...prev,
										startDateTimeISO: data.startTime,
										endDateTimeISO: data.endTime ?? undefined,
										publicIdentifier: data.publicIdentifier,
										parcsCodes: data.parcsCodes,
										publicAccessToken: data.publicAccessToken ?? "",
										vehicles: data.permit_vehicles_vehicle.map((vehicle) => ({
											id: vehicle.vehicleId,
											plate: vehicle.vehicle.plate,
											state: vehicle.vehicle.state,
										})),
										permitStatus: data.permitStatus,
										permitIntervals: data.permitIntervals.map((i) => ({
											startDateTimeISO: i.startDateTime,
											endDateTimeISO: i.endDateTime,
											schedules: i.schedules,
										})),
									}));
									setIsProcessingPermit(false);
								}
							},
							onError: (error) => {
								setError(error);
								setIsProcessingPermit(false);
							},
						},
					);
				}
			} else {
				setIsProcessingPermit(false);
			}
		},
		setDuplicatedPermit: (permit) => {
			setDuplicatedPermit({
				...permit,
				permitIntervals: permit.permitIntervals,
				timezone,
				publicIdentifier: permit.publicIdentifier,
			});
		},
	});
	const lastFormStepId = steps[steps.length - 2]!.id;
	const isLastFormStep = activeStepId === lastFormStepId;
	const step = steps.find(
		(x) => x.id === (isProcessingPermit && activeStepId === "confirmation" ? lastFormStepId : activeStepId),
	);
	const isConfirmationStep = step?.id === "confirmation";
	const isTermsStep = step?.id === "terms";
	const isLastTermsStep = isTermsStep && step.isLastTerms;
	const hasAcceptedAllTerms = isTermsStep && step.hasAcceptedAllTerms;
	const currentStepIndex = steps.findIndex((s) => s.id === activeStepId);
	const nextStep =
		currentStepIndex >= 0 && currentStepIndex < steps.length - 1 ? steps[currentStepIndex + 1] : undefined;
	const arrivedViaRef = startOverLookupId !== undefined && startOverLookupId !== entryLookupId;
	const showStartOver = isKiosk && (currentStepIndex > 0 || arrivedViaRef);
	useEffect(() => {
		if (error) {
			errorRef.current?.focus();
		}
	}, [error]);
	const getButtonLabel = () => {
		if (!step) {
			return t`Next`;
		}
		if (isLastFormStep && isLastTermsStep && isTermsStep && hasAcceptedAllTerms) {
			return t`Park`;
		}
		if (isLastFormStep && isLastTermsStep && isTermsStep && !hasAcceptedAllTerms) {
			return t`Accept and Park`;
		}
		if (isLastFormStep && isTermsStep) {
			return step.primaryActionLabel ?? t`Next`;
		}
		if (isLastFormStep && step.id !== "payment") {
			return t`Park`;
		}
		if (nextStep?.title && step.primaryActionLabel && (!isTermsStep || isLastTermsStep)) {
			return `${step.primaryActionLabel} - ${nextStep.title}`;
		}
		return step.primaryActionLabel ?? t`Next`;
	};
	const totalSteps = steps.length - 1;
	const currentStepNumber = currentStepIndex + 1;
	const ruleErrorMessage =
		error?.data?.rule && error.data.errorTypeI18n
			? getRuleErrorDescriptor(error.data.rule, error.data.errorTypeI18n)
			: undefined;
	const ruleErrorValues = formatRuleMessageValues({
		context: error?.data?.context,
		i18n,
		locale: i18n.locale,
		timezone,
	});
	return (
		<div className="flex h-full flex-col lg:h-auto print:h-auto">
			<div aria-live="polite" className="sr-only">
				{step?.title &&
					!isConfirmationStep &&
					t`Step ${String(currentStepNumber)} of ${String(totalSteps)}: ${step.title}`}
			</div>
			<div className="flex h-full flex-col justify-between gap-8 lg:h-auto print:h-auto">
				{!step ? (
					<div className="p-4 md:p-8">
						<NotFoundAlert />
					</div>
				) : !isConfirmationStep ? (
					<section
						ref={sectionRef}
						aria-label={t`Step ${String(currentStepNumber)} of ${String(totalSteps)}: ${step.title}`}
					>
						<ParkingSummary timezone={permitType.timezone} hideLocation={!!preSelectedLocationId} />
						{SignInPrompt && optionalParkerAccounts && !isKiosk && <SignInPrompt />}
						<div className="p-4 md:p-8">
							<div className="space-y-4">
								{error && (
									<div ref={errorRef} tabIndex={-1}>
										<FormErrorAlert serverError={error} timezone={timezone} />
									</div>
								)}
								<div ref={stepContentRef}>{step.component}</div>
								{!!step.secondaryAction && (
									<ButtonRow className="flex-col gap-4 md:flex-row">
										{showStartOver && <KioskStartOver isDisabled={isProcessingPermit} />}
										<Button
											isLoading={isProcessingPermit}
											type="button"
											intent="secondary"
											size="lg"
											className={"w-full md:w-auto"}
											onClick={step.secondaryAction.onClick}
										>
											{step.secondaryAction.label}
										</Button>
										<Button
											isLoading={isProcessingPermit}
											type="submit"
											form={formId}
											size="lg"
											className={"w-full md:w-auto"}
										>
											{getButtonLabel()}
										</Button>
									</ButtonRow>
								)}
								{!step.secondaryAction && (!step.hidePrimaryAction || showStartOver) && (
									<ButtonRow className="flex-col gap-4 md:flex-row">
										{showStartOver && <KioskStartOver isDisabled={isProcessingPermit} />}
										{!step.hidePrimaryAction && (
											<Button
												isLoading={isProcessingPermit}
												type="submit"
												form={formId}
												size="lg"
												className={"w-full md:w-auto"}
											>
												{getButtonLabel()}
											</Button>
										)}
									</ButtonRow>
								)}
							</div>
						</div>
					</section>
				) : (
					<div className="space-y-8">
						{error ? (
							<div ref={errorRef} tabIndex={-1} className="p-4 md:p-8">
								{error.data?.rule ? (
									<GeneralErrorAlert
										icon={NoParkingIcon}
										title={t`Unable to Complete Registration`}
										message={String(
											ruleErrorMessage
												? i18n._(ruleErrorMessage.id, ruleErrorValues, {
														message: ruleErrorMessage.message,
													})
												: t`Unable to complete registration`,
										)}
										showContactSupport={false}
										customAction={{
											label: t`Start Over`,
											onClick: () => window.location.reload(),
										}}
									/>
								) : (
									<GeneralErrorAlert
										title={t`Unable to Complete Registration`}
										referenceId={error.data?.referenceId}
										customAction={{
											label: t`Start Over`,
											onClick: () => window.location.reload(),
										}}
									/>
								)}
							</div>
						) : (
							step.component
						)}
					</div>
				)}
			</div>

			{duplicatedPermit && (
				<ExistingPermitModal
					plate={duplicatedPermit.plate}
					permitIntervals={duplicatedPermit.permitIntervals}
					timezone={timezone!}
					publicIdentifier={duplicatedPermit.publicIdentifier}
					locationContact={permitType.contact}
					onClose={() => {
						setDuplicatedPermit(undefined);
						setIsProcessingPermit(false);
						goToStep(lastFormStepId);
					}}
				/>
			)}
		</div>
	);
};
